import { useCallback, useMemo, useState } from 'react';

import PropTypes from 'prop-types';
import { BsArrowRepeat, BsDownload, BsListUl, BsStack } from 'react-icons/bs';

import AddToContactListModal from '#contactListFeature/components/AddToContactListModal/AddToContactListModal';
import CampaignCSVExportModal from '#emailCampaignFeature/components/statistics/CampaignCSVExportModal';
import Button from '#globalComponents/Button/Button';
import Card from '#globalComponents/Card/Card';
import RequireCapability from '#globalComponents/RequireCapability/RequireCapability';
import ActionScopeButton from '#globalComponents/Table/actions/ActionScopeButton';
import BulkActionsBar from '#globalComponents/Table/actions/BulkActionsBar';
import FilterChipsBar from '#globalComponents/Table/FilterChipsBar';
import TableSearchInput from '#globalComponents/Table/filters/TableSearchInput';
import { useScopedSelection } from '#globalHooks/useScopedSelection';
import { useUrlFilters } from '#globalHooks/useUrlFilters';
import { buildFilterChips } from '#lib/tableFilters';

import { POST_CSV_COLUMN_GROUPS, POST_CSV_DEFAULT_COLUMNS } from '../../../constants/postMailingCsvExportColumns';
import { POST_SEND_FILTER_SCHEMA, POST_SENDS_URL_NAMESPACE } from '../../../constants/postMailingSendFilters';
import { useExportPostMailingCsv, useRefreshPostMailingStatus } from '../../../models/campaigns/mutations';
import { usePostMailingSends, usePostMailingSendsStats } from '../../../models/campaigns/queries';
import PostMailingQueryError from '../PostMailingQueryError';

import PostMailingBlockedRecipientsHint from './PostMailingBlockedRecipientsHint';
import PostMailingReviewPanel from './PostMailingReviewPanel';
import PostMailingSendsTable from './PostMailingSendsTable';
import PostMailingStatCards from './PostMailingStatCards';

const PAGE_SIZE = 50;
const EMPTY_STATS = { total: 0, sent: 0, processing: 0, returned: 0, failed: 0, scanned: 0 };

const formatCount = (count) => count.toLocaleString('de-DE');

const LETTER_ENTITY_LABELS = { singular: 'Brief', plural: 'Briefe' };

/**
 * True if a designData object contains a CTA banner with its QR enabled (in any zone, incl. container
 * children) — i.e. a block that produces a trackable QR scan.
 * @param {object} [designData]
 * @returns {boolean}
 */
function designHasQrBlock(designData) {
	let found = false;
	const walk = (v) => {
		if (found || !v) return;
		if (Array.isArray(v)) return v.forEach(walk);
		if (typeof v === 'object') {
			if (v.type === 'callToAction' && v.showQr) found = true;
			else Object.values(v).forEach(walk);
		}
	};
	walk(designData?.zones);
	return found;
}

const NoSendsYet = () => (
	<Card>
		<div className="text-center py-12">
			<div className="w-14 h-14 bg-bgBase-400 rounded-full flex items-center justify-center mx-auto mb-4">
				<BsStack className="w-7 h-7 text-bgBase-800" />
			</div>
			<h3 className="text-md font-semibold mb-1">Noch keine Versanddaten</h3>
			<p className="text-sm text-bgBase-800">Statistiken erscheinen, sobald die Kampagne versendet wurde.</p>
		</div>
	</Card>
);

const PostMailingStatisticsTab = ({ campaignId, campaignName, hasSends, campaignStatus, ebriefJobId, designData }) => {
	const [addToListRequest, setAddToListRequest] = useState(null);
	const [exportRequest, setExportRequest] = useState(null);
	const { filters, filtersKey, isFiltered, setFilters, removeFilters, clearFilters, page, setPage } = useUrlFilters(
		POST_SEND_FILTER_SCHEMA,
		{ namespace: POST_SENDS_URL_NAMESPACE },
	);

	const {
		data: statsData,
		isFetching: isFetchingStats,
		isError: isStatsError,
		error: statsError,
		refetch: refetchStats,
	} = usePostMailingSendsStats(campaignId, { enabled: hasSends });
	const {
		data: sendsData,
		isLoading: isLoadingSends,
		isFetching: isFetchingSends,
		isError: isSendsError,
		error: sendsError,
		refetch: refetchSends,
	} = usePostMailingSends(campaignId, { filters, page, pageSize: PAGE_SIZE, enabled: hasSends });
	const refreshStatus = useRefreshPostMailingStatus();
	const exportCsv = useExportPostMailingCsv();

	const stats = statsData?.stats ?? EMPTY_STATS;
	const sends = useMemo(() => sendsData?.sends ?? [], [sendsData?.sends]);
	const filteredTotal = sendsData?.filteredTotal ?? 0;

	// Show QR stats when the design has a QR block, or as a fallback if any letter was already scanned.
	const showQr = useMemo(() => designHasQrBlock(designData) || stats.scanned > 0, [designData, stats.scanned]);

	const { count, clear, tableSelection, toActionScope, actionCount, actionScopeLabel, entityLabel } =
		useScopedSelection(sends, {
			totalCount: filteredTotal,
			filters,
			resetKey: `${campaignId}:${filtersKey}`,
			entityLabel: { singular: 'Brief', plural: 'Briefe', gender: 'm' },
		});

	const openAddToList = () =>
		setAddToListRequest({
			scope: { table: 'postSends', contextId: campaignId, ...toActionScope() },
			count: actionCount,
		});
	const openExport = () => setExportRequest({ scope: toActionScope('sendIds'), count: actionCount });

	const chips = useMemo(() => buildFilterChips(filters, POST_SEND_FILTER_SCHEMA), [filters]);
	const handleSearchChange = useCallback((search) => setFilters({ search }), [setFilters]);

	// While submission to eBrief is still in progress no status data exists, so no refresh is available.
	const isSubmitting = campaignStatus === 'sending' && !ebriefJobId;

	// Pull the real eBrief job state first (the cache refetch alone wouldn't reflect provider-side changes).
	// The mutation invalidates the sends + stats caches on success, so they refetch on their own.
	const handleRefresh = async () => {
		if (isSubmitting) return;
		try {
			await refreshStatus.mutateAsync(campaignId);
		} catch {
			// Error toast handled by the global MutationCache; still refetch to show whatever we have.
			refetchSends();
		}
	};
	const refreshing = refreshStatus.isPending || isFetchingStats || isFetchingSends;

	if (!hasSends) return <NoSendsYet />;

	return (
		<div className="space-y-6">
			<PostMailingReviewPanel campaignId={campaignId} enabled={hasSends} />

			{isStatsError ? (
				<Card>
					<PostMailingQueryError
						error={statsError}
						title="Statistiken konnten nicht geladen werden"
						onRetry={refetchStats}
					/>
				</Card>
			) : (
				<div className="space-y-2">
					<PostMailingStatCards stats={stats} showQr={showQr} />
					<PostMailingBlockedRecipientsHint campaignId={campaignId} blockedCount={statsData?.blockedCount} />
				</div>
			)}

			<Card>
				<div className="flex items-center justify-between mb-4">
					<h3 className="text-md font-semibold tracking-tight">Versandstatus</h3>
					<Button onClick={handleRefresh} variant="outline" size="sm" disabled={refreshing || isSubmitting}>
						<BsArrowRepeat className={`h-4 w-4 mr-2 ${refreshing || isSubmitting ? 'animate-spin' : ''}`} />
						{isSubmitting ? 'Wird übermittelt...' : refreshing ? 'Aktualisiere...' : 'Aktualisieren'}
					</Button>
				</div>

				{isSendsError ? (
					<PostMailingQueryError
						error={sendsError}
						title="Versanddaten konnten nicht geladen werden"
						onRetry={refetchSends}
					/>
				) : (
					<>
						<TableSearchInput
							value={filters.search}
							onChange={handleSearchChange}
							placeholder="Empfänger oder Firma suchen …"
							className="mb-3 w-full max-w-xs"
						/>
						<FilterChipsBar
							chips={chips}
							resultLabel={`${formatCount(filteredTotal)} von ${formatCount(stats.total)} Briefen`}
							onRemove={removeFilters}
							onClearAll={clearFilters}
						/>
						<PostMailingSendsTable
							sends={sends}
							campaignId={campaignId}
							showQrColumn={showQr}
							filters={filters}
							onFilterChange={setFilters}
							statusCounts={statsData?.statusCounts}
							isLoading={isLoadingSends}
							emptyLabel={
								isFiltered
									? 'Keine Briefe entsprechen den Filtern.'
									: 'Noch keine Versanddaten vorhanden.'
							}
							pagination={{
								currentPage: page,
								totalPages: Math.ceil(filteredTotal / PAGE_SIZE),
								onPageChange: setPage,
								totalItems: filteredTotal,
								pageSize: PAGE_SIZE,
							}}
							selection={tableSelection}
							actions={{
								label: actionScopeLabel,
								buttons: (
									<>
										<ActionScopeButton
											icon={<BsListUl />}
											onClick={openAddToList}
											disabled={actionCount === 0}
										>
											Zu Kontaktliste
										</ActionScopeButton>
										<RequireCapability required="leads.export">
											<ActionScopeButton
												icon={<BsDownload />}
												onClick={openExport}
												disabled={actionCount === 0}
											>
												CSV Export
											</ActionScopeButton>
										</RequireCapability>
									</>
								),
							}}
							bulkBar={<BulkActionsBar selectedCount={count} entityLabel={entityLabel} onClear={clear} />}
						/>
					</>
				)}
			</Card>

			{exportRequest && (
				<CampaignCSVExportModal
					isOpen
					onClose={() => setExportRequest(null)}
					campaignName={campaignName}
					scope={exportRequest.scope}
					count={exportRequest.count}
					columnGroups={POST_CSV_COLUMN_GROUPS}
					defaultColumns={POST_CSV_DEFAULT_COLUMNS}
					entityLabels={LETTER_ENTITY_LABELS}
					onExport={(columns) =>
						exportCsv.mutateAsync({ campaignId, options: { columns, ...exportRequest.scope } })
					}
					isExporting={exportCsv.isPending}
				/>
			)}

			{addToListRequest && (
				<AddToContactListModal
					isOpen
					onClose={() => setAddToListRequest(null)}
					tableScope={addToListRequest.scope}
					contactCount={addToListRequest.count}
					entityLabels={LETTER_ENTITY_LABELS}
					onSuccess={clear}
				/>
			)}
		</div>
	);
};

PostMailingStatisticsTab.propTypes = {
	campaignId: PropTypes.string.isRequired,
	campaignName: PropTypes.string,
	hasSends: PropTypes.bool.isRequired,
	campaignStatus: PropTypes.string,
	ebriefJobId: PropTypes.string,
	designData: PropTypes.object,
};

export default PostMailingStatisticsTab;
